import React, { useState, useEffect } from "react";
import { useAuth } from "../../context/AuthContext";
import AdminLayout from "../../layout/admin";

// Component Imports
import GenericDataLayout from "../../components/custom/GenericDataLayout"; // Adjust path if necessary
import GenericTable from "../../components/custom/GenericTable"; // Adjust path if necessary
import { getMethodApiCall } from "src/services/api";
import { ALL_FILTER } from "../../utils/helpers";

const Instructors = () => {
  const { token } = useAuth();
  
  // State
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState([]);
  const [districts, setDistricts] = useState([]); // List for district dropdown
  const [itis, setItis] = useState([]); // List for ITI dropdown
  
  // Pagination & Filter State
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    totalRecords: 0,
    totalPages: 1,
  });
  
  const [filters, setFilters] = useState({
    district: ALL_FILTER,
    iti_id: ALL_FILTER,
  });

  // --- Column Configuration ---
    const columns = [
      { header: "District",  accessor: "iti_master.district", render: (row) => row.iti_master?.district || '-'},
      { header: "ITI Name", accessor: "iti_master.iti_name", render: (row) => row.iti_master?.iti_name || '-' },
      { header: "Trainer Name", accessor: "name" },
      { header: "Phone Number", accessor: "phone_number" },
      {
        header: "Last Login Date",
        accessor: "last_login_date",
        render: (row) => {
          if (!row.last_login_date) return "Never";
          const d = new Date(row.last_login_date);
          const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
          return `${months[d.getMonth()]} ${d.getDate()}, ${d.getFullYear()} ${d.getHours().toString().padStart(2, '0')}:${d.getMinutes().toString().padStart(2, '0')}`;
        },
      },
      {
        header: "Last Login Days",
        accessor: "last_login_days",
        render: (row) => {
          if (!row.last_login_date) return "None";
          const loginDate = new Date(row.last_login_date);
          const today = new Date();
          const diffTime = Math.abs(today - loginDate);
          const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
          if (diffDays === 0) return "Today";
          if (diffDays === 1) return "1 day ago";
          return `${diffDays} days ago`;
        },
      },
    ];

  // --- API Calls ---

  // 1. Fetch Dropdown Data (Districts)
  const fetchDistrictsAndITIs = async () => {
    try {
      const res = await getMethodApiCall(`/v1/api/trainee/get_district_picklist`);
      const resData = res.data;
      if (resData.success) {
        setDistricts(resData?.data || []);
        setItis([]); // ITIs will be loaded when district is selected
      }
    } catch (error) {
      console.error("Error fetching districts:", error);
    }
  };

  // Fetch ITIs for selected district
  const fetchItisForDistrict = async (district) => {
    try {
      if (!district || district === ALL_FILTER) {
        setItis([]);
        return;
      }
      const res = await getMethodApiCall(`/v1/api/trainee/get_iti_picklist/${encodeURIComponent(district)}`);
      if (res?.status === 200 && res.data?.success) {
        setItis(res.data.data || []);
      }
    } catch (err) {
      console.error('Error fetching itis for district', err);
      setItis([]);
    }
  };

  // 2. Fetch Table Data
  const fetchData = async () => {
    if (!token) return;
    
    setLoading(true);
    try {
      const queryParams = new URLSearchParams();
      queryParams.append("page", pagination.page.toString());
      queryParams.append("limit", pagination.limit.toString());
      
      if (filters.district && filters.district !== ALL_FILTER) {
        queryParams.append("district", filters.district.trim());
      }
      if (filters.iti_id && filters.iti_id !== ALL_FILTER) {
        queryParams.append("iti_id", filters.iti_id);
      }

      const response = await getMethodApiCall(`/v1/api/admin/instructor-presence?${queryParams}`);
      
      const responseData = response.data;
      if (response.status === 200 && responseData?.data) {
        setData(responseData.data || []);
        setPagination((prev) => ({
          ...prev,
          totalRecords: responseData.pagination?.total || 0,
          totalPages: responseData.pagination?.totalPages || 1,
        }));
      }
    } catch (error) {
      console.error("Failed to fetch instructor data", error);
    } finally {
      setLoading(false);
    }
  };

  // --- Effects ---

  // Initial Load (Dropwdowns)
  useEffect(() => {
    if (token) {
      fetchDistrictsAndITIs();
    }
  }, [token]);

  // Data Fetching (Triggers on Page, Limit, or Filter change)
  useEffect(() => {
    if (token) {
      fetchData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, pagination.page, pagination.limit, filters]);

  // --- Handlers ---

  const handlePageChange = (newPage) => {
    setPagination((prev) => ({ ...prev, page: newPage }));
  };

  const handleLimitChange = (newLimit) => {
    setPagination((prev) => ({ ...prev, limit: newLimit, page: 1 })); // Reset to page 1 on limit change
  };

  const handleFilterChange = (key, value) => {
    // If district changes, reset dependent ITI
    if (key === 'district') {
      setFilters((prev) => ({ ...prev, district: value, iti_id: ALL_FILTER }));
      setPagination((prev) => ({ ...prev, page: 1 }));
      // Only fetch ITIs if a specific district is selected (not all)
      if (value && value !== ALL_FILTER) {
        fetchItisForDistrict(value);
      } else {
        setItis([]);
      }
      return;
    }
    setFilters((prev) => ({ ...prev, [key]: value }));
    setPagination((prev) => ({ ...prev, page: 1 })); // Reset to page 1 on filter change
  };

  const handleResetFilters = () => {
    setFilters({ district: ALL_FILTER, iti_id: ALL_FILTER });
    setPagination((prev) => ({ ...prev, page: 1 }));
    setItis([]); // Clear ITIs on reset
  };

  // --- Layout Configuration ---

  const filterConfig = [
    {
      type: "select",
      label: "District",
      value: filters.district,
      options: districts.map((d) => ({
        label: d.district,
        value: d.district,
      })),
      onChange: (val) => handleFilterChange("district", val),
    },
    {
      type: "select",
      label: "ITI Name",
      value: filters.iti_id,
      options: itis.map((item) => ({
        label: item.iti_name,
        value: item.id, // Use item.id instead of item.iti_id for consistent value handling
      })),
      onChange: (val) => handleFilterChange("iti_id", val), // Change to iti_id to match backend expectation
      disabled: !filters.district || filters.district === ALL_FILTER,
    },
  ];

  return (
    <AdminLayout>
      <GenericDataLayout
        title="Trainers" // Or "Instructors" based on preference
        subtitle="Manage and view instructor details"
        isLoading={loading}
        filters={filterConfig}
        onResetFilters={handleResetFilters}
        pagination={{
          page: pagination.page,
          limit: pagination.limit,
          totalRecords: pagination.totalRecords,
          onPageChange: handlePageChange,
          onLimitChange: handleLimitChange,
        }}
      >
        <GenericTable 
          columns={columns} 
          data={data} 
          pagination={pagination}
        />
      </GenericDataLayout>
    </AdminLayout>
  );
};

export default Instructors;